import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { useApprovalContext } from '../hooks/useApprovalContext';
import { useAddTeamApproversFooter } from '../hooks/useAddTeamApproversFooter';
import { useFetchUsersByScope } from '../hooks/useFetchUsersByScope';
import { useTeamMemberCounts } from '../hooks/useTeamMemberCounts';
import { maxNumberOfApprovers } from '../utils/approvalModalUtils';
import { buildGroupedApproverOptions } from '../utils/approverSelectUtils';
import { ACTOR } from '../constants/actors';
import { serializeActor, parseActorKeys } from 'ui-product-approvals-common/utils/actorKey';
import { jsx as _jsx } from "react/jsx-runtime";
export const ApproverSelectInput = ({
  approvers,
  value,
  onChange,
  isLoading,
  label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.approvers"
  }),
  help,
  tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.ApprovalModal.approverTooltip"
  }),
  required = true,
  disabled,
  errorMessage
}) => {
  var _approval$meta$curren;
  const approval = useApprovalContext();
  const currentUserId = (_approval$meta$curren = approval.meta.currentUser) === null || _approval$meta$curren === void 0 ? void 0 : _approval$meta$curren.user_id;
  const {
    users: superAdminUsers
  } = useFetchUsersByScope('super-admin');
  const teamMemberCounts = useTeamMemberCounts(approvers);
  const superAdminUserIds = useMemo(() => new Set(superAdminUsers.map(user => String(user.id))), [superAdminUsers]);
  const options = useMemo(() => buildGroupedApproverOptions({
    approvers: approvers.filter(approver => approver.actor.actorType !== ACTOR.USER || approver.actor.id !== String(currentUserId)),
    superAdminUserIds,
    teamMemberCounts
  }), [approvers, superAdminUserIds, currentUserId, teamMemberCounts]);
  const dropdownFooter = useAddTeamApproversFooter(approvers, {
    enabled: !isLoading && !errorMessage
  });
  const onChangeApprover = evt => {
    onChange(parseActorKeys(evt.target.value));
  };
  const hasTooManyApprovers = value.length > maxNumberOfApprovers;
  const validationMessage = errorMessage !== null && errorMessage !== void 0 ? errorMessage : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.approvalModalSelectError",
    options: {
      maxNumberOfApprovers,
      count: value.length - maxNumberOfApprovers
    }
  });
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    help: help,
    required: required,
    tooltipProps: {
      use: 'longform'
    },
    tooltip: tooltip,
    error: Boolean(errorMessage) || hasTooManyApprovers,
    validationMessage: (Boolean(errorMessage) || hasTooManyApprovers) && validationMessage,
    children: /*#__PURE__*/_jsx(Select, {
      anchorType: "button",
      isLoading: isLoading,
      disabled: disabled,
      "data-test-id": "product-approvals-approver-select",
      value: value.map(serializeActor),
      onChange: onChangeApprover,
      options: options,
      multi: true,
      multiCollapseLimit: 3,
      dropdownFooter: dropdownFooter,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.selectApprover"
      })
    })
  });
};